import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  useMediaQuery,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { fullName } from '../../domain/Adopters/AccountDisplay'
import { getReadableAnimalAdoptionStatus } from '../../domain/Animal/AnimalDisplay'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import {
  AdopterDto,
  AnimalAdoptionDtoOfferType,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDto,
} from '../../interactors/gen/backendClient'
import { useAdoptersStore } from '../../store/AdoptersStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../utils/hooks/useDialogOpened'
import { AxiosError } from 'axios'
import { useMutation } from 'react-query'
import { readableAxiosError } from '../../utils/axios'
import { theme } from '../theme'
import { useAccountsStore } from '../../store/AccountsStore'
import { useStripeConnectStatus } from '../../utils/hooks/useStripeConnectStatus'
import { StripeConnectRequiredStep } from './StripeConnectRequiredStep'
import { AdoptionInfoStep } from './FastLaunchProcedureSteps/AdoptionInfoStep'
import type { AdoptionContext } from './FastLaunchProcedureDialog'
import { suspendConflictingAdoptionAttempts } from '../../utils/suspendConflictingAdoptionAttempts'
import { createAdoptionAttempt } from '../../domain/Adopters/createAdoptionAttempt'
import { GuestAdoptionLinkDto } from '../../interactors/clients/GuestAdoptionsClient'
import { GuestAdoptionLinkDialog } from './GuestAdoption/GuestAdoptionLinkDialog'

interface Props {
  animalId: string | null
}

export const NewAdoptionAttemptConfirmModal: React.FC<Props> = ({ animalId }) => {
  const queryParams = new URLSearchParams(location.search)
  const adopterId = queryParams.get('adopterId')
  const sendEmailToAdopter = queryParams.get('sendEmailToAdopter')

  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const navigate = useNavigate()
  const globalSnackBarStore = useGlobalSnackbarStore()
  const adoptersStore = useAdoptersStore()
  const accountsStore = useAccountsStore()
  const isPremium = accountsStore.isPremium()
  const [animal, setAnimal] = useState<AnimalDto | null>(null)
  const [animalAdopter, setAnimalAdopter] = useState<AdopterDto | undefined>(undefined)
  const [lockState, setLockState] = useState(false)
  const [step, setStep] = useState<1 | 2>(1)
  const [adoptionContext, setAdoptionContext] = useState<AdoptionContext | null>(null)
  const [guestAdoptionLink, setGuestAdoptionLink] = useState<GuestAdoptionLinkDto | null>(null)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { status: stripeStatus, refresh: refreshStripeStatus } = useStripeConnectStatus(isDialogOpen && step === 2)

  const prevAnimalId = useRef<string | null>(animalId)

  useEffect(() => {
    if (animalId === prevAnimalId.current) {
      return
    }

    if (animalId) {
      const loadAnimal = async () => {
        await AnimalsClient.getAnimal(animalId)
          .then((animal) => {
            if (
              animal.adoption.adopterId &&
              adoptersStore.adopters
                .find((adopter) => adopter.id === animal.adoption.adopterId)
                ?.adoptionAttempts?.some((attempt) => attempt.animalId === animal.id)
            ) {
              setAnimalAdopter(adoptersStore.adopters.find((adopter) => adopter.id === animal.adoption.adopterId))
            }
            setAnimal(animal)
          })
          .catch((_error) => {
            globalSnackBarStore.triggerErrorMessage(`L'animal n'a pas été trouvé.`)
          })
      }
      loadAnimal()
    }

    prevAnimalId.current = animalId
    setStep(1)
    setAdoptionContext(null)
    setLockState(false)
  }, [animalId])

  /**
   * The price chosen on the first step wins over the one stored on the animal, which is only
   * written to the animal further down: eligibility has to be decided on what was just entered.
   */
  const animalWithContextPrice = (): Pick<AnimalDto, 'adoption'> | null => {
    if (!animal) return null
    if (adoptionContext?.offerType === undefined) return animal

    const priceInEuros =
      adoptionContext.offerType === AnimalAdoptionDtoOfferType.Donation ? 0 : adoptionContext.priceInEuros

    return { adoption: { ...animal.adoption, priceInEuros } }
  }

  const goToAdoption = (attemptId: string) => {
    navigate(`/suivi-adoptant/${queryParams.get('adopterId')}/?tab=ongoing-adoption&attemptId=${attemptId}`)
  }

  const newAdoptionAttemptMutation = useMutation(
    async ({ adopterId, animalId }: { adopterId: string; animalId: string }) =>
      await createAdoptionAttempt({
        adopterId,
        animalId,
        shouldPrepareDocuments: adoptionContext?.prepareDocuments ?? isPremium,
        sendEmailToAdopter: sendEmailToAdopter === 'true',
        animal: animalWithContextPrice(),
        isStripeConnectReady: stripeStatus === 'connected',
        hasDeposit: adoptionContext?.hasDeposit,
        depositAmount: adoptionContext?.depositAmount,
        depositDate: adoptionContext?.depositDate,
        depositStatus: adoptionContext?.depositStatus,
      }),
    {
      onSuccess: (result) => {
        adoptersStore.fetchOrRefreshAdopters()
        // The payment link has to be handed over before leaving: the procedure opens once it is closed.
        if (result.guestLink) {
          setGuestAdoptionLink(result.guestLink)
          return
        }
        goToAdoption(result.attemptId)
        globalSnackBarStore.triggerSuccessMessage(`Nouvelle procédure d'adoption créée avec succès.`)
      },
      onError: (error: Error | AxiosError) => {
        globalSnackBarStore.triggerErrorMessage(readableAxiosError(error).join(' '))
      },
    }
  )

  const onSubmit = async () => {
    setLockState(true)
    if (!adoptionContext) {
      globalSnackBarStore.triggerErrorMessage(`Veuillez renseigner les informations d'adoption.`)
      return
    }
    if (stripeStatus !== 'connected') {
      return
    }
    if (adopterId && animalId) {
      if (adopter?.adoptionAttempts?.find((attempt) => attempt.animalId === animalId)) {
        return globalSnackBarStore.triggerErrorMessage(`L'adoptant a déjà une procédure d'adoption pour cet animal.`)
      }
      await suspendConflictingAdoptionAttempts({
        adopters: adoptersStore.adopters,
        currentAdopterId: adopterId,
        animalId,
      })
      setAnimalAdoptionStatusToInTheProcessOfBeingAdoptable()
      newAdoptionAttemptMutation.mutate({ adopterId: adopterId, animalId: animalId })
    } else {
      globalSnackBarStore.triggerErrorMessage(`L'adoptant n'a pas été trouvé.`)
    }
    closeDialog()
  }

  const setAnimalAdoptionStatusToInTheProcessOfBeingAdoptable = () => {
    if (!animal) return
    if (adoptionContext?.offerType) {
      animal.adoption.offerType = adoptionContext.offerType
      const priceFromContext =
        adoptionContext.offerType === AnimalAdoptionDtoOfferType.Donation ? 0 : adoptionContext.priceInEuros
      if (adoptionContext.offerType === AnimalAdoptionDtoOfferType.FreeAmount) {
        animal.adoption.priceInEuros = priceFromContext
      } else if (priceFromContext !== undefined && !Number.isNaN(priceFromContext)) {
        animal.adoption.priceInEuros = priceFromContext
      }
    }
    animal.adoption.status = AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
    animal.adoption.step = AnimalAdoptionDtoStep.AdoptionToBeValidated
    animal.adoption.adoptionDate = undefined
    animal.adoption.exitDate = undefined
    animal.adoption.vpaDate = undefined
    if (adopterId) {
      animal.adoption.adopterId = adopterId
    }
    AnimalsClient.editAnimal(animal)
  }

  useEffect(() => {
    if (animalId) {
      openDialog()
    }
  }, [animalId])

  const adopter = adoptersStore.adopters.find((anAdopter) => anAdopter.id === adopterId)

  if (guestAdoptionLink) {
    return (
      <GuestAdoptionLinkDialog
        link={guestAdoptionLink}
        collectedDeposit={adoptionContext?.depositStatus ? adoptionContext?.depositAmount : undefined}
        onDone={() => {
          const attemptId = guestAdoptionLink.attemptId
          setGuestAdoptionLink(null)
          goToAdoption(attemptId)
        }}
      />
    )
  }

  return (
    <Dialog
      open={isDialogOpen}
      onClose={closeDialog}
      PaperProps={{
        style: { borderRadius: 15, padding: 15 },
      }}
    >
      {step === 1 ? (
        <AdoptionInfoStep
          submitStep={({ adoptionContext }) => {
            setAdoptionContext(adoptionContext)
            setStep(2)
          }}
          close={closeDialog}
          isPremium={isPremium}
        />
      ) : (
        <>
          <DialogContent sx={{ pb: isMobile ? 0 : 'auto' }}>
            {stripeStatus === 'loading' ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                padding={3}
                sx={{ width: '100%', height: '100%' }}
              >
                <CircularProgress />
              </Box>
            ) : stripeStatus === 'missing' || stripeStatus === 'error' ? (
              <StripeConnectRequiredStep
                isAdmin={accountsStore.isAdmin()}
                onClose={closeDialog}
                onRefresh={refreshStripeStatus}
              />
            ) : (
              animal && (
                <Box>
                  <Typography variant="h4" component="h2" marginBottom={4} fontSize={20}>
                    {`Vous avez sélectionné ${animal.name} comme étant le souhait d'adoption de ${
                      adopter && fullName(adopter)
                    }`}
                  </Typography>
                  {(lockState || animal.adoption.status !== AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable) && (
                    <Typography variant="body2" component="p" marginBottom={4} fontSize={15} color="black">
                      <span style={{ fontWeight: 700 }}>
                        <Link style={{ color: 'black' }} to={`/animaux/${animal.id}`}>
                          {animal.name}
                        </Link>{' '}
                        {` est actuellement enregistré comme: ${
                          animal.adoption.status
                            ? `"${getReadableAnimalAdoptionStatus(animal.adoption.status)}"`
                            : 'Erreur'
                        }.`}
                      </span>
                      <br />
                      {`Souhaitez vous confirmer le souhait d'adoption ? Si oui il sera alors considéré comme "En cours d'adoption" et directement associé à la nouvelle procédure d'adoption de ${
                        adopter ? fullName(adopter) : ''
                      }.`}
                    </Typography>
                  )}
                  {animalAdopter && animalAdopter.id !== adopter?.id && (
                    <Typography variant="body2" component="p" marginBottom={4} fontSize={15} color="black">
                      <span style={{ fontWeight: 700 }}>
                        <Link style={{ color: 'black' }} to={`/animaux/${animal.id}`}>
                          {animal.name}
                        </Link>{' '}
                        {` a déjà une procédure d'adoption lancée avec: ${fullName(animalAdopter)}`}
                      </span>
                      <br />
                      {`Si vous continuez, cette procédure sera suspendue et une nouvelle procédure sera créée avec ${
                        adopter ? fullName(adopter) : ''
                      }.`}
                    </Typography>
                  )}
                </Box>
              )
            )}
          </DialogContent>
          <DialogActions>
            <Box
              display="flex"
              flexDirection={isMobile ? 'column' : 'row'}
              width="100%"
              gap={1}
              justifyContent={isMobile ? 'auto' : 'flex-end'}
            >
              <Button
                variant="contained"
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  textTransform: 'none',
                  fontSize: isMobile ? '14px' : '16px',
                  px: isMobile ? 'auto' : '50px',
                }}
                onClick={() => closeDialog()}
              >
                Annuler
              </Button>
              {stripeStatus === 'connected' && (
                <LoadingButton
                  data-testid="apply-changes"
                  variant="contained"
                  sx={{
                    width: isMobile ? '100%' : 'auto',
                    textTransform: 'none',
                    fontSize: isMobile ? '14px' : '16px',
                  }}
                  onClick={() => onSubmit()}
                >
                  {lockState ||
                  animal?.adoption.status !== AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable ||
                  (animalAdopter && animalAdopter.id !== adopter?.id)
                    ? 'Valider quand même'
                    : 'Valider'}
                </LoadingButton>
              )}
            </Box>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
